import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const useAccountSettings = ({ getAuthHeaders, customerDetails, fetchCustomerDetails, fetchQuotaUsage }) => {
  const [togglingAgent, setTogglingAgent] = useState(false);
  const [togglingArchive, setTogglingArchive] = useState(false);
  const [mailingListLoading, setMailingListLoading] = useState(false);
  const [mailingListSubscribed, setMailingListSubscribed] = useState(null);
  const [mailingListSubmitting, setMailingListSubmitting] = useState(false);
  const [mailingListError, setMailingListError] = useState('');
  const [billingPortalLoading, setBillingPortalLoading] = useState(false);
  const [billingPortalError, setBillingPortalError] = useState('');

  const API_BACKEND = process.env.REACT_APP_SHOPKEEP_BACKEND || 'http://localhost:3001';

  const agentEnabled = customerDetails?.agentEnabled ?? false;
  const archiveSoldItems = customerDetails?.settings?.find(s => s.key === 'archiveSoldItems')?.value ?? false;

  const handleToggleAgent = useCallback(async () => {
    setTogglingAgent(true);
    try {
      const headers = await getAuthHeaders();
      await axios.patch(
        `${API_BACKEND}/api/agent/settings`,
        { enabled: !agentEnabled },
        headers
      );
      await Promise.all([fetchCustomerDetails(), fetchQuotaUsage()]);
    } catch (err) {
      console.error('Error updating agent settings:', err);
    } finally {
      setTogglingAgent(false);
    }
  }, [getAuthHeaders, API_BACKEND, agentEnabled, fetchCustomerDetails, fetchQuotaUsage]);

  const handleToggleArchiveSoldItems = useCallback(async () => {
    setTogglingArchive(true);
    try {
      const headers = await getAuthHeaders();
      await axios.put(
        `${API_BACKEND}/api/account/settings`,
        { settings: [{ key: 'archiveSoldItems', value: !archiveSoldItems }] },
        headers
      );
      await fetchCustomerDetails();
    } catch (err) {
      console.error('Error updating archive setting:', err);
    } finally {
      setTogglingArchive(false);
    }
  }, [getAuthHeaders, API_BACKEND, archiveSoldItems, fetchCustomerDetails]);

  const fetchMailingListStatus = useCallback(async () => {
    setMailingListLoading(true);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(`${API_BACKEND}/api/mailing-list/status`, headers);
      if (response.data.success) {
        setMailingListSubscribed(response.data.data.isSubscribed);
      }
    } catch (err) {
      console.error('Error fetching mailing list status:', err);
    } finally {
      setMailingListLoading(false);
    }
  }, [getAuthHeaders, API_BACKEND]);

  const handleToggleMailingList = useCallback(async () => {
    setMailingListSubmitting(true);
    setMailingListError('');
    const endpoint = mailingListSubscribed ? 'unsubscribe' : 'subscribe';
    const ownerEmail = customerDetails?.email || 'N/A';
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(
        `${API_BACKEND}/api/mailing-list/${endpoint}`,
        { email: ownerEmail },
        headers
      );
      if (response.data.success) {
        setMailingListSubscribed(!mailingListSubscribed);
      } else {
        setMailingListError(response.data.error || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      console.error(`Error ${endpoint} mailing list:`, err);
      setMailingListError('Unable to update your preference at this time. Please try again later.');
    } finally {
      setMailingListSubmitting(false);
    }
  }, [getAuthHeaders, API_BACKEND, mailingListSubscribed, customerDetails?.email]);

  const handleOpenBillingPortal = useCallback(async () => {
    setBillingPortalLoading(true);
    setBillingPortalError('');
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(
        `${API_BACKEND}/api/checkout/billing-portal`,
        { returnUrl: window.location.origin },
        headers
      );
      if (response.data.success) {
        window.location.href = response.data.data.url;
      } else {
        setBillingPortalError(response.data.error || 'Unable to open billing portal. Please try again.');
      }
    } catch (err) {
      console.error('Error opening billing portal:', err);
      setBillingPortalError(
        err.response?.data?.error || 'Unable to open billing portal. Please try again later.'
      );
    } finally {
      setBillingPortalLoading(false);
    }
  }, [getAuthHeaders, API_BACKEND]);

  useEffect(() => {
    if (customerDetails) {
      fetchMailingListStatus();
    }
  }, [customerDetails, fetchMailingListStatus]);

  return {
    agentEnabled,
    archiveSoldItems,
    togglingAgent,
    togglingArchive,
    mailingListLoading,
    mailingListSubscribed,
    mailingListSubmitting,
    mailingListError,
    handleToggleAgent,
    handleToggleArchiveSoldItems,
    handleToggleMailingList,
    billingPortalLoading,
    billingPortalError,
    handleOpenBillingPortal,
  };
};

export default useAccountSettings;
